import {
  ProjectChangeType,
  type ProjectChange,
  type ProjectChangeMap,
} from '@shared/helpers/pipeline/update-engine.models';

/**
 * Pre-DEV-3293 discriminants that may still exist on {@link Baseline.actions}.
 * See migration `20260520120000-rename-legacy-element-recipe-change-type`.
 */
export const LEGACY_ELEMENT_RECIPE_TYPE = 'element.recipe' as const;
export const LEGACY_ELEMENT_RECIPE_OVERWRITE_TYPE =
  'element.recipe.overwrite' as const;

type LegacyRecipeChange = {
  type: typeof LEGACY_ELEMENT_RECIPE_TYPE;
  elementId: string;
  recipeId?: string;
  recipe?: ProjectChangeMap[ProjectChangeType.ElementRecipeApply]['recipe'];
  fallbackName?: string;
};

type LegacyRecipeOverwriteChange = {
  type: typeof LEGACY_ELEMENT_RECIPE_OVERWRITE_TYPE;
  elementId: string;
  recipe: ProjectChangeMap[ProjectChangeType.ElementRecipeApply]['recipe'];
};

const asLegacyRecipeChange = (
  change: ProjectChange,
): LegacyRecipeChange | undefined => {
  const candidate = change as unknown as { type?: string };
  if (candidate.type !== LEGACY_ELEMENT_RECIPE_TYPE) return undefined;
  return change as unknown as LegacyRecipeChange;
};

const asLegacyRecipeOverwriteChange = (
  change: ProjectChange,
): LegacyRecipeOverwriteChange | undefined => {
  const candidate = change as unknown as { type?: string };
  if (candidate.type !== LEGACY_ELEMENT_RECIPE_OVERWRITE_TYPE) return undefined;
  return change as unknown as LegacyRecipeOverwriteChange;
};

/**
 * Convert a legacy recipe change to {@link ProjectChangeType.ElementRecipeApply}.
 *
 * @returns Upgraded change, or `undefined` when the payload cannot be salvaged.
 */
export const upgradeLegacyRecipeProjectChange = (
  change: LegacyRecipeChange | LegacyRecipeOverwriteChange,
): ProjectChangeMap[ProjectChangeType.ElementRecipeApply] | undefined => {
  const recipe = change.recipe;
  if (!recipe) return undefined;

  const recipeId =
    change.type === LEGACY_ELEMENT_RECIPE_OVERWRITE_TYPE
      ? recipe.id
      : (change.recipeId ?? recipe.id);
  if (!recipeId) return undefined;

  return {
    type: ProjectChangeType.ElementRecipeApply,
    elementId: change.elementId,
    recipeId,
    recipe,
  };
};

/**
 * Rewrites legacy `element.recipe` / `element.recipe.overwrite` rows to
 * {@link ProjectChangeType.ElementRecipeApply}. Unsalvageable rows are dropped.
 */
export const upgradeLegacyRecipeChanges = (
  changes: ProjectChange[],
): ProjectChange[] => {
  const next: ProjectChange[] = [];
  for (const change of changes) {
    const legacy =
      asLegacyRecipeChange(change) ?? asLegacyRecipeOverwriteChange(change);
    if (!legacy) {
      next.push(change);
      continue;
    }
    const upgraded = upgradeLegacyRecipeProjectChange(legacy);
    if (upgraded) next.push(upgraded);
  }
  return next;
};
